// src/services/knowledge-base/exceptions/exceptionsApi.ts
import { AxiosResponse } from 'axios';
import deviceInfoAxios from '../../../utils/axios/deviceInfo.axios';
import { DEVICE_INFO_API_URL } from '../../../config';

export type ExceptionStatus = 'pending' | 'in_progress' | 'resolved';
export type KnowledgeScope = 'general' | 'client_specific';
export type ExceptionCategory =
    | 'Hotel Term'
    | 'Contradiction'
    | 'Synonym'
    | 'Duplicate'
    | 'Overlap'
    | 'Missing Info'
    | 'Inconsistency';

export type KBException = {
    _id: string;
    hotelSub: string;
    reportId: string;
    targetFileName: string;
    exceptionNumber: number;
    title: string;
    category: ExceptionCategory;
    status: ExceptionStatus;
    sofiaObservation: string;
    context: { text: string; snippet?: string };
    refs: {
        newRef: { fileName: string; chunkIndex: number };
        otherRef?: {
            fileName?: string;
            document_url?: string;
            page_number?: number;
            chunkIndex?: number;
        };
        confidence?: number;
        pineconeScore?: number;
    };
    document: { name: string; url?: string };
    resolution?: {
        mode: 'text' | 'confirm';
        confirmationType?: string;
        explanation: string;
        scope: KnowledgeScope;
        additionalNotes?: string;
        resolvedBy: { userId: string; username?: string; displayName?: string };
        resolvedAt: string;
    };
    createdAt?: string;
    updatedAt?: string;
};

// If you have a dedicated AI base URL, swap this:
// const BASE_URL = `${AI_SERVICE_API_URL}/knowledge-base/exceptions`;
const BASE_URL = `${DEVICE_INFO_API_URL}/knowledge-base/exceptions`;

// Query params for list
export type ExceptionsListQuery = {
    status?: ExceptionStatus | 'all' | '';
    category?: ExceptionCategory | 'all' | '';
    reportId?: string;
};

const buildListUrl = (query?: ExceptionsListQuery) => {
    if (!query) return BASE_URL;

    const qs = new URLSearchParams();

    if (query.status && query.status !== 'all') qs.set('status', query.status);
    if (query.category && query.category !== 'all')
        qs.set('category', query.category);
    if (query.reportId && query.reportId.trim())
        qs.set('reportId', query.reportId.trim());

    const queryString = qs.toString();
    return queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
};

// GET /knowledge-base/exceptions?status=&category=&reportId=
const tryToFetchKnowledgeBaseExceptions = (
    query?: ExceptionsListQuery,
): Promise<AxiosResponse<KBException[]>> => {
    const url = buildListUrl(query);
    return deviceInfoAxios.get<KBException[]>(url);
};

// GET /knowledge-base/exceptions/:id
const tryToFetchKnowledgeBaseExceptionById = (
    id: string,
): Promise<AxiosResponse<KBException>> => {
    return deviceInfoAxios.get<KBException>(`${BASE_URL}/${id}`);
};

// PATCH /knowledge-base/exceptions/:id/status  { status }
const tryToUpdateKnowledgeBaseExceptionStatus = (data: {
    id: string;
    status: ExceptionStatus;
}): Promise<AxiosResponse<KBException>> => {
    return deviceInfoAxios.patch<KBException>(`${BASE_URL}/${data.id}/status`, {
        status: data.status,
    });
};

// PATCH /knowledge-base/exceptions/:id/resolve
// { explanation, scope, additionalNotes? }
const tryToResolveKnowledgeBaseException = (data: {
    id: string;
    explanation: string;
    scope: KnowledgeScope;
    additionalNotes?: string;
}): Promise<AxiosResponse<KBException>> => {
    return deviceInfoAxios.patch<KBException>(
        `${BASE_URL}/${data.id}/resolve`,
        {
            explanation: data.explanation,
            scope: data.scope,
            additionalNotes: data.additionalNotes,
        },
    );
};

const tryToResolveKnowledgeBaseExceptionConfirm = (data: {
    id: string;
    scope: KnowledgeScope;
    confirmationType: string; // e.g. 'agree', 'accept_new', 'mark_not_issue'
}): Promise<AxiosResponse<KBException>> => {
    return deviceInfoAxios.patch<KBException>(
        `${BASE_URL}/${data.id}/resolve-confirm`,
        {
            scope: data.scope,
            confirmationType: data.confirmationType,
        },
    );
};

const tryToConfirmKnowledgeBaseException = (data: {
    id: string;
    confirmationType?: string;
}): Promise<AxiosResponse<KBException>> => {
    return deviceInfoAxios.patch<KBException>(
        `${BASE_URL}/${data.id}/confirm`,
        { confirmationType: data.confirmationType },
    );
};

// PATCH /knowledge-base/exceptions/:id/confirm
const tryToConfirmResolveKnowledgeBaseException = (data: {
    id: string;
    confirmationType?: string; // e.g. 'confirm' | 'accept_new'
}): Promise<AxiosResponse<KBException>> => {
    return deviceInfoAxios.patch<KBException>(
        `${BASE_URL}/${data.id}/confirm`,
        { confirmationType: data.confirmationType },
    );
};

export const KnowledgeBaseExceptionsService = {
    tryToFetchKnowledgeBaseExceptions,
    tryToFetchKnowledgeBaseExceptionById,
    tryToUpdateKnowledgeBaseExceptionStatus,
    tryToResolveKnowledgeBaseException,
    tryToResolveKnowledgeBaseExceptionConfirm,
    tryToConfirmKnowledgeBaseException,
    tryToConfirmResolveKnowledgeBaseException,
};
